/**
 * Coût estimé des agents : logique pure. Le serveur chiffre chaque modèle
 * tarifé d'après la consommation mesurée (`GET /v1/llm-settings/cost-estimates`) ;
 * l'écran relit la valeur du modèle choisi, sans recopier la formule.
 */
import type { LlmCostEstimates, LlmRole, LlmSettings, ModelCost } from "../../models/domain";
import { modelKey } from "./llmCatalog";
import { effectiveValue, reviewEnabled } from "./llmSettings";

/** Ce que coûte une unité de ce rôle avec ce couple fournisseur/modèle ; `null` = rien de mesuré. */
export const roleCost = (
  estimates: LlmCostEstimates | null,
  role: LlmRole,
  provider: string | undefined,
  model: string | undefined,
): ModelCost | null => {
  if (!estimates || !provider || !model?.trim()) return null;
  return estimates.roles[role]?.by_model[modelKey(provider, model.trim())] ?? null;
};

/** Les rôles qui composent le coût d'une leçon (objectifs), d'un objectif extrait (faits), et d'une quête servie. */
export type UnitKind = "objectives" | "facts" | "quests";

export const UNIT_ROLES: Record<UnitKind, readonly LlmRole[]> = {
  objectives: ["objective_extraction"],
  facts: ["fact_extraction", "fact_review"],
  quests: ["quest_generation", "quest_review"],
};

/**
 * Le coût d'une leçon (`objectives`), d'un objectif extrait (`facts`) ou d'une quête servie (`quests`) pour
 * une matière (`""` = le global), avec ses modèles EFFECTIFS. Une relecture
 * coupée ne coûte rien. `null` dès qu'un rôle actif n'est pas chiffrable : un
 * total partiel se lirait comme un total.
 */
export const unitCost = (
  estimates: LlmCostEstimates | null,
  llm: LlmSettings,
  subjectId: string,
  kind: UnitKind,
): ModelCost | null => {
  let usd = 0;
  let measured = true;
  for (const role of UNIT_ROLES[kind]) {
    if ((role === "fact_review" || role === "quest_review") && !reviewEnabled(llm, role)) continue;
    const cost = roleCost(
      estimates,
      role,
      effectiveValue(llm, subjectId, role, "provider").value as string | undefined,
      effectiveValue(llm, subjectId, role, "model").value as string | undefined,
    );
    if (!cost) return null;
    usd += cost.usd;
    measured = measured && cost.measured;
  }
  return { usd, measured };
};

const usdFormat = new Intl.NumberFormat("fr-FR", { maximumSignificantDigits: 2 });

/** « 0,0067 $ » — deux chiffres significatifs : c'est une estimation. */
export const formatUsd = (usd: number) => `${usdFormat.format(usd)} $`;

/** « 6,7 $ les 1 000 » : l'ordre de grandeur qu'un dirigeant compare. */
export const formatPerThousand = (usd: number) => `${usdFormat.format(usd * 1000)} $ les 1 000`;

/** Deux estimations sont-elles différentes, à l'affichage près ? */
export const differentCost = (a: ModelCost | null, b: ModelCost | null) =>
  (a === null) !== (b === null) || (a !== null && b !== null && formatUsd(a.usd) !== formatUsd(b.usd));
